module Halogen.Styled.DSL
  ( AnimStep
  , StyleSheet
  , animation
  , inline
  , rawSheet
  , scopedSheet
  , select
  , select_
  , step
  ) where

import Prelude

import Data.Foldable (fold)
import Data.Maybe (Maybe(..))
import Data.String (toLower)
import Data.String as Str
import Data.String.Regex as Regex
import Data.String.Regex.Flags (global)
import Data.String.Regex.Unsafe (unsafeRegex)
import Data.Tuple.Nested (type (/\), (/\))
import Foreign.Object (Object, fromHomogeneous)
import Foreign.Object as Obj
import Halogen.Styled (SheetTemplate(..), UpdateOpts, inlineRaw, scopedSheetRaw)
import Partial.Unsafe (unsafeCrashWith)
import Type.Row.Homogeneous (class Homogeneous)

-------------------------------------------------------------------------------
-- Constants
-------------------------------------------------------------------------------

uniqueIdent :: String
uniqueIdent = "$unique"

scopedSheet :: forall ctx r a. StyleSheet -> UpdateOpts ctx r a
scopedSheet ss = scopedSheetRaw uniqueIdent $ printStyleSheet ss

data StyleSheet
  = RuleSet { selector :: String, declarations :: Object String }
  | MediaRule String StyleSheet
  | AtKeyframes String (Array AnimStep)
  | Raw String
  | Cat StyleSheet StyleSheet
  | Empty

instance Monoid StyleSheet where
  mempty = Empty

instance Semigroup StyleSheet where
  append = Cat

printStyleSheet :: StyleSheet -> SheetTemplate
printStyleSheet = SheetTemplate <<< go
  where
  go = case _ of
    RuleSet { selector, declarations } -> selector <> " " <> "{" <> printDecls declarations <> "}"
    MediaRule rule stmt -> rule <> "{" <> go stmt <> "}"
    AtKeyframes rule steps -> "@keyframes " <> rule <> "{" <> Str.joinWith "\n" (map printAnimStep steps) <> "}"
    Raw str -> str
    Cat a b -> go a <> "\n" <> go b
    Empty -> ""

printAnimStep :: AnimStep -> String
printAnimStep (AnimStep name decls) = name <> "{" <> printDecls decls <> "}"

printDecls :: Object String -> String
printDecls obj = Str.joinWith "" $ map printDecl $ Obj.toUnfoldable obj
  where
  printDecl :: String /\ String -> String
  printDecl (k /\ v) = camelToKebab k <> ": " <> v <> ";"

select :: forall decls. Homogeneous decls String => String -> { | decls } -> StyleSheet
select selector sheet = RuleSet
  { selector: "." <> uniqueIdent <> selector
  , declarations: fromHomogeneous sheet
  }

select_ :: forall decls. Homogeneous decls String => { | decls } -> StyleSheet
select_ sheet = select "" sheet

data AnimStep = AnimStep String (Object String)

step :: forall decls. Homogeneous decls String => String -> { | decls } -> AnimStep
step name sheet = AnimStep name $ fromHomogeneous sheet

animation :: Array AnimStep -> StyleSheet
animation steps = fold
  [ AtKeyframes uniqueIdent steps
  , select_
      { animationName: uniqueIdent
      }
  ]

inline :: forall decls ctx r a. Homogeneous decls String => { | decls } -> UpdateOpts ctx r a
inline r = inlineRaw $ printDecls $ fromHomogeneous r

rawSheet :: String -> StyleSheet
rawSheet = Raw

-------------------------------------------------------------------------------
-- Utils
-------------------------------------------------------------------------------

camelToKebab :: String -> String
camelToKebab = Regex.replace' (unsafeRegex "([a-z])([A-Z])" global)
  ( \_ -> case _ of
      [ Just x, Just y ] -> x <> "-" <> toLower y
      _ -> unsafeCrashWith "camelToKebab: impossible"
  )
